import React from "react";
import { Typography, Box, Chip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

import { FILE_SIZE_LIMIT_LABEL } from "../../constants/file-upload-limits";

export interface FileUploadInfoProps {
  type: "image" | "video" | "content" | "avatar" | "banner" | "any";
  maxSize?: string;
  recommendedSize?: string;
  className?: string;
}

const FileUploadInfo: React.FC<FileUploadInfoProps> = ({
  type,
  maxSize,
  recommendedSize,
  className,
}) => {
  const getFileInfo = () => {
    switch (type) {
      case "any":
        return {
          formats: ["ANY"],
          maxSize: maxSize || FILE_SIZE_LIMIT_LABEL.DEFAULT,
          recommendedSize: recommendedSize || "Зависит от типа файла",
          note: "Поддерживаются все форматы",
        };
      case "image":
        return {
          formats: ["JPG", "PNG", "WebP"],
          maxSize: maxSize || FILE_SIZE_LIMIT_LABEL.DEFAULT,
          recommendedSize: recommendedSize || "1920x1080 пикселей",
          note: "Рекомендуется использовать изображения в соотношении 16:9",
        };
      case "video":
        return {
          formats: ["MP4", "WebM", "MOV"],
          maxSize: maxSize || FILE_SIZE_LIMIT_LABEL.CONTENT_AND_VIDEO,
          recommendedSize: recommendedSize || "1920x1080 пикселей, 30 FPS",
          note: "Рекомендуется битрейт до 5 Мбит/с",
        };
      case "content":
        return {
          formats: ["ANY"],
          maxSize: maxSize || FILE_SIZE_LIMIT_LABEL.CONTENT_AND_VIDEO,
          recommendedSize: recommendedSize || "Зависит от типа файла",
          note: "Поддерживаются все форматы",
        };
      case "avatar":
        return {
          formats: ["JPG", "PNG", "WebP"],
          maxSize: maxSize || FILE_SIZE_LIMIT_LABEL.DEFAULT,
          recommendedSize: recommendedSize || "300x300 пикселей",
          note: "Рекомендуется квадратное изображение",
        };
      case "banner":
        return {
          formats: ["JPG", "PNG", "WebP"],
          maxSize: maxSize || FILE_SIZE_LIMIT_LABEL.DEFAULT,
          recommendedSize: recommendedSize || "1600x900 пикселей",
          note: "Рекомендуется соотношение 2:1",
        };
      default:
        return {
          formats: ["JPG", "PNG"],
          maxSize: maxSize || FILE_SIZE_LIMIT_LABEL.DEFAULT,
          recommendedSize: recommendedSize || "1920x1080 пикселей",
          note: "",
        };
    }
  };

  const fileInfo = getFileInfo();
  const isAllFormats =
    fileInfo.formats.length === 1 && fileInfo.formats[0] === "ANY";

  return (
    <Box
      className={className}
      sx={{
        mt: 1,
        p: 2,
        borderRadius: 1,
        backgroundColor: "#444",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <InfoIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" color="primary">
          Требования к файлу
        </Typography>
      </Box>

      {isAllFormats ? (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1, fontStyle: "italic" }}
        >
          💡 {fileInfo.note}
        </Typography>
      ) : (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            <strong>Поддерживаемые форматы:</strong>
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
            {fileInfo.formats.map((format) => (
              <Chip
                key={format}
                label={format}
                size="small"
                variant="outlined"
                color="primary"
              />
            ))}
          </Box>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" display="block">
        <strong>Максимальный размер:</strong> {fileInfo.maxSize}
      </Typography>

      <Typography variant="caption" color="text.secondary" display="block">
        <strong>Рекомендуемое разрешение:</strong> {fileInfo.recommendedSize}
      </Typography>

      {fileInfo.note && !isAllFormats && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 0.5, fontStyle: "italic" }}
        >
          💡 {fileInfo.note}
        </Typography>
      )}
    </Box>
  );
};

export default FileUploadInfo;
